import { useCallback, useEffect, useId, useRef, useState } from "react";

import { useAddressAutocomplete } from "../../hooks/useAddressAutocomplete";
import { reverseGeocodeCoordinates } from "../../utils/reverseGeocode";
import styles from "./LocationAutocomplete.module.css";

const MIN_QUERY_LENGTH = 3;

function IconSearch() {
  return (
    <svg
      className={styles.searchIconSvg}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function IconMapPin() {
  return (
    <svg
      className={styles.stateIconSvg}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 10c0 4.99-5.53 10.2-7.42 11.84a.9.9 0 0 1-1.16 0C9.53 20.2 4 14.99 4 10a8 8 0 1 1 16 0Z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function IconWifi() {
  return (
    <svg
      className={styles.stateIconSvg}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="1" y1="1" x2="23" y2="23" />
      <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
      <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
      <path d="M10.71 5.05A16 16 0 0 1 22.56 9" />
      <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
      <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
      <line x1="12" y1="20" x2="12.01" y2="20" />
    </svg>
  );
}

function IconCrosshair() {
  return (
    <svg
      className={styles.gpsIconSvg}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
      <circle cx="12" cy="12" r="8" />
    </svg>
  );
}

const SUPPORTS_GEO = typeof navigator !== "undefined" && "geolocation" in navigator;

export default function LocationAutocomplete({
  value,
  inputClassName,
  placeholder,
  maxLength,
  disabled,
  ariaInvalid,
  countryCode,
  searchProvider = "default",
  appearance = "default",
  onChange,
  onSelect,
  onBlur,
}) {
  const { suggestions, isSearching, error, search, clearSuggestions } =
    useAddressAutocomplete({ countryCode, provider: searchProvider });

  const [isOpen, setIsOpen]           = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isLocating, setIsLocating]   = useState(false);
  const [gpsError, setGpsError]       = useState(false);
  const containerRef = useRef(null);
  const listId       = useId();

  const hasActiveQuery = typeof value === "string" && value.length >= MIN_QUERY_LENGTH;

  // Ouvre le dropdown pour résultats, skeleton et états vide/erreur
  useEffect(() => {
    if (!hasActiveQuery) {
      setIsOpen(false);
      setActiveIndex(-1);
      return;
    }

    if (suggestions.length > 0 || isSearching || error !== null) {
      setIsOpen(true);
      if (suggestions.length > 0) setActiveIndex(-1);
    } else {
      setIsOpen(false);
    }
  }, [suggestions, isSearching, error, hasActiveQuery]);

  useEffect(() => {
    function handlePointerDown(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setActiveIndex(-1);
      }
    }

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, []);

  const handleGeolocate = useCallback(() => {
    if (!SUPPORTS_GEO || isLocating) return;
    setIsLocating(true);
    setGpsError(false);
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        const lng = coords.longitude;
        const lat = coords.latitude;
        try {
          const result = await reverseGeocodeCoordinates([lng, lat]);
          const suggestion = {
            name: result.address || result.city || "",
            label: result.label || "",
            address: result.address || "",
            city: result.city || "",
            country: "",
            coordinates: [lng, lat],
            resolved: true,
          };
          onChange(suggestion.name || suggestion.city);
          clearSuggestions();
          setIsOpen(false);
          onSelect(suggestion);
        } catch {
          setGpsError(true);
        } finally {
          setIsLocating(false);
        }
      },
      () => {
        setIsLocating(false);
        setGpsError(true);
      },
      { timeout: 8000 }
    );
  }, [isLocating, onChange, clearSuggestions, onSelect]);

  const handleChange = useCallback(
    (e) => {
      const next = e.target.value;
      if (gpsError) setGpsError(false);
      onChange(next);
      search(next);
    },
    [onChange, search, gpsError]
  );

  const handleSelect = useCallback(
    (suggestion) => {
      onChange(suggestion.name || suggestion.label);
      clearSuggestions();
      setIsOpen(false);
      setActiveIndex(-1);
      onSelect(suggestion);
    },
    [onChange, clearSuggestions, onSelect]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (!isOpen) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, -1));
      } else if (e.key === "Enter" && activeIndex >= 0) {
        e.preventDefault();
        handleSelect(suggestions[activeIndex]);
      } else if (e.key === "Escape") {
        setIsOpen(false);
        setActiveIndex(-1);
      }
    },
    [isOpen, suggestions, activeIndex, handleSelect]
  );

  const handleBlur = useCallback(
    (e) => {
      if (containerRef.current?.contains(e.relatedTarget)) return;
      setIsOpen(false);
      setActiveIndex(-1);
      onBlur?.();
    },
    [onBlur]
  );

  return (
    <div ref={containerRef} className={`${styles.container} ${appearance === "editor" ? styles.editorAppearance : ""}`}>

      <span className={styles.searchIconWrap}>
        <IconSearch />
      </span>

      <input
        type="text"
        className={inputClassName}
        style={{ paddingLeft: "54px", paddingRight: SUPPORTS_GEO ? "54px" : undefined }}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={placeholder}
        maxLength={maxLength}
        disabled={disabled}
        autoComplete="off"
        aria-invalid={ariaInvalid}
        role="combobox"
        aria-expanded={isOpen}
        aria-autocomplete="list"
        aria-controls={listId}
        aria-activedescendant={activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
      />

      {isSearching ? (
        <span
          className={styles.spinner}
          style={SUPPORTS_GEO ? { right: "54px" } : undefined}
          aria-hidden="true"
        />
      ) : null}

      {SUPPORTS_GEO ? (
        <button
          type="button"
          className={[
            styles.gpsButton,
            isLocating ? styles.gpsButtonLocating : "",
            gpsError   ? styles.gpsButtonError   : "",
          ].join(" ").trim()}
          onClick={handleGeolocate}
          disabled={isLocating || disabled}
          aria-label="Utiliser ma position actuelle"
          tabIndex={-1}
        >
          <IconCrosshair />
        </button>
      ) : null}

      {isOpen ? (
        <ul id={listId} role="listbox" className={styles.dropdown}>
          {isSearching ? (
            <>
              <li className={styles.skeletonRow} aria-hidden="true">
                <span className={styles.skeletonLine} style={{ width: "68%" }} />
                <span className={styles.skeletonLineSm} style={{ width: "42%" }} />
              </li>
              <li className={styles.skeletonRow} aria-hidden="true">
                <span className={styles.skeletonLine} style={{ width: "52%" }} />
                <span className={styles.skeletonLineSm} style={{ width: "36%" }} />
              </li>
              <li className={styles.skeletonRow} aria-hidden="true">
                <span className={styles.skeletonLine} style={{ width: "75%" }} />
                <span className={styles.skeletonLineSm} style={{ width: "48%" }} />
              </li>
            </>
          ) : error === "network" ? (
            <li className={styles.stateRow} aria-live="polite">
              <span className={styles.stateIconWrap}><IconWifi /></span>
              <span className={styles.stateText}>Service indisponible — vérifie ta connexion</span>
            </li>
          ) : error === "empty" ? (
            <li className={styles.stateRow} aria-live="polite">
              <span className={styles.stateIconWrap}><IconMapPin /></span>
              <span className={styles.stateText}>Aucun lieu trouvé — essaie une adresse plus précise</span>
            </li>
          ) : (
            suggestions.map((suggestion, index) => (
              <li
                key={`${suggestion.coordinates[0]}-${suggestion.coordinates[1]}-${index}`}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={index === activeIndex}
                className={`${styles.suggestion} ${
                  index === activeIndex ? styles.suggestionActive : ""
                }`.trim()}
                onPointerDown={(e) => {
                  e.preventDefault();
                  handleSelect(suggestion);
                }}
              >
                <span className={styles.suggestionName}>
                  {suggestion.name || suggestion.label}
                </span>
                {suggestion.address || suggestion.city ? (
                  <span className={styles.suggestionMeta}>
                    {[suggestion.address, suggestion.city].filter(Boolean).join(", ")}
                  </span>
                ) : null}
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}
